Woolman.Utils.prepareQueryParams();
Woolman.Collection = document.querySelector('[data-ajax-parent]');

const CLIENT_SORT_PAGE_SIZE = 30;

/**
 * Computes the discount ratio (0-1) for a product card based on its price data attributes.
 * @param {HTMLElement} card - Product card element with data-price / data-compare-at-price.
 * @returns {number}
 */
const computeCardDiscountRatio = (card) => {
  const price = parseFloat(card.getAttribute('data-price') || '0');
  const compareAtPrice = parseFloat(card.getAttribute('data-compare-at-price') || '0');
  if (!compareAtPrice || compareAtPrice <= price) return 0;
  return (compareAtPrice - price) / compareAtPrice;
};

/**
 * Returns the brand grouping key for a product card (first word of its vendor, lowercased).
 * @param {HTMLElement} card - Product card element with data-vendor.
 * @returns {string}
 */
const cardBrandKey = (card) => (card.getAttribute('data-vendor') || '').trim().split(' ')[0].toLowerCase();

/**
 * Returns all cards sorted by biggest discount first.
 * @param {HTMLElement[]} cards
 * @returns {HTMLElement[]}
 */
const sortCardsByDiscount = (cards) => [...cards].sort((a, b) => computeCardDiscountRatio(b) - computeCardDiscountRatio(a));

/**
 * Returns all cards reordered so distinct brands appear first (one per brand, preserving
 * each brand's original rank), then the rest in their original order. Used on Sale
 * collections so a single heavily-discounted brand doesn't dominate the best-selling view.
 * @param {HTMLElement[]} cards
 * @returns {HTMLElement[]}
 */
const sortCardsByVendorDiversity = (cards) => {
  const seenBrands = new Set();
  const firstPerBrand = [];
  const remaining = [];
  cards.forEach((card) => {
    const brandKey = cardBrandKey(card);
    if (!seenBrands.has(brandKey)) {
      seenBrands.add(brandKey);
      firstPerBrand.push(card);
    } else {
      remaining.push(card);
    }
  });
  return firstPerBrand.concat(remaining);
};

// Holds the full, custom-sorted card list for the active client-side sort (discount or
// vendor-diversified best-selling), so pagination between pages needs no extra fetch.
let clientSortState = null;

/**
 * Renders the pager for a client-side sorted grid, reusing the theme's pagination markup
 * so it looks identical to native Shopify pagination.
 * @param {number} totalPages
 * @param {number} currentPage
 * @param {string} sortByValue
 */
const renderClientPagination = (totalPages, currentPage, sortByValue) => {
  const container = document.querySelector('#pagination-ajax');
  if (!container) return;
  if (totalPages <= 1) {
    container.innerHTML = '';
    return;
  }

  const pageHref = (page) => {
    const params = new URLSearchParams(Shopify.queryParams);
    params.set('sort_by', sortByValue);
    if (page > 1) {
      params.set('cpage', page);
    } else {
      params.delete('cpage');
    }
    return location.pathname + '?' + params.toString().replaceAll(/%2C/g, ',');
  };

  let parts = '';
  for (let page = 1; page <= totalPages; page++) {
    if (page === currentPage) {
      parts += `<span class="pagination-item current-page" aria-current="page"><span>${page}</span></span>`;
    } else {
      parts += `<a href="${pageHref(page)}" class="pagination-item page-link" data-client-page="${page}"><span>${page}</span></a>`;
    }
  }

  const prevHtml = currentPage > 1
    ? `<a href="${pageHref(currentPage - 1)}" class="pagination-item arrow-link arrow-link--prev" data-client-page="${currentPage - 1}">${'&lsaquo;'}</a>`
    : `<span class="pagination-item arrow-link arrow-link--prev disabled">${'&lsaquo;'}</span>`;

  const nextHtml = currentPage < totalPages
    ? `<a href="${pageHref(currentPage + 1)}" class="pagination-item arrow-link arrow-link--next" data-client-page="${currentPage + 1}">${'&rsaquo;'}</a>`
    : `<span class="pagination-item arrow-link arrow-link--next disabled">${'&rsaquo;'}</span>`;

  container.innerHTML = `<nav class="pagination pagination-type--pages mt-48 mb-48 size--tiny" role="navigation" aria-label="Pagination">${prevHtml}<div class="pagination-parts">${parts}</div>${nextHtml}</nav>`;
};

/**
 * Displays one page of the active client-sorted card list in the grid and refreshes the pager.
 * @param {HTMLElement} grid
 * @param {number} page
 */
const renderClientPage = (grid, page) => {
  if (!grid || !clientSortState) return;
  const totalPages = Math.max(1, Math.ceil(clientSortState.cards.length / CLIENT_SORT_PAGE_SIZE));
  const clampedPage = Math.min(Math.max(page, 1), totalPages);
  clientSortState.page = clampedPage;

  const start = (clampedPage - 1) * CLIENT_SORT_PAGE_SIZE;
  const pageCards = clientSortState.cards.slice(start, start + CLIENT_SORT_PAGE_SIZE);

  Array.from(grid.children).forEach((el) => el.remove());
  pageCards.forEach((card) => grid.appendChild(card));

  renderClientPagination(totalPages, clampedPage, clientSortState.sortBy);

  if (clampedPage > 1) {
    Shopify.queryParams.cpage = clampedPage;
  } else {
    delete Shopify.queryParams.cpage;
  }
  window.history.replaceState('', '', location.pathname + '?' + getCollectionURLSearchParams());
};

/**
 * After a grid has been (re)populated, checks whether the active sort needs client-side
 * sorting (discount, or brand-diversified best-selling on Sale collections). The full
 * matching set is already present in the DOM (server renders up to 250 per page for these
 * sorts), so this only reorders and paginates locally without another fetch.
 * @param {string} [selector] - Selector for the grid container.
 */
const applyClientSortAndPaginate = (selector) => {
  const grid = document.querySelector(selector || '#product-grid-ajax');
  if (!grid) {
    clientSortState = null;
    return;
  }

  const isDiscountSort = Shopify.queryParams.sort_by === 'discount-descending';
  const isBestsellerDiversify = grid.dataset.c4ySaleContext === 'true' && Shopify.queryParams.sort_by === 'best-selling';

  if (!isDiscountSort && !isBestsellerDiversify) {
    clientSortState = null;
    return;
  }

  const cards = Array.from(grid.children).filter((el) => el.hasAttribute('data-price') || el.hasAttribute('data-vendor'));
  if (!cards.length) {
    clientSortState = null;
    return;
  }

  const sortedCards = isDiscountSort ? sortCardsByDiscount(cards) : sortCardsByVendorDiversity(cards);
  const requestedPage = parseInt(Shopify.queryParams.cpage, 10) || 1;

  clientSortState = { sortBy: Shopify.queryParams.sort_by, cards: sortedCards, page: requestedPage };
  renderClientPage(grid, requestedPage);
};

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-client-page]');
  if (!link) return;
  event.preventDefault();
  const grid = document.querySelector('#product-grid-ajax');
  renderClientPage(grid, parseInt(link.dataset.clientPage, 10));
  if (grid) {
    const top = grid.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top, behavior: 'smooth' });
  }
});

/**
 * Generates search params from form data for filtering collection or search results.
 * @param {HTMLElement} formElement - Form element to turn into search params.
 * @returns
 */
const generateSearchParamsFromFormData = (formElement) => {
  const checkboxesToKeep = {};
  for (const element of formElement.elements) {
    const key = element.getAttribute('name');
    if (!key || !key.includes('filter')) continue;

    const value = element.value;
    const inputType = element.getAttribute('type');

    if (value === '') continue;

    // If this is a price range input, and both gte and lte are at their min/max, skip them
    if (
      (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') &&
      formElement.querySelector('[name="filter.v.price.gte"]') &&
      formElement.querySelector('[name="filter.v.price.lte"]')
    ) {
      const minInput = formElement.querySelector('[name="filter.v.price.gte"]');
      const maxInput = formElement.querySelector('[name="filter.v.price.lte"]');
      const minValue = minInput ? minInput.getAttribute('min') : null;
      const maxValue = maxInput ? maxInput.getAttribute('max') : null;
      const valGte = minInput ? minInput.value : null;
      const valLte = maxInput ? maxInput.value : null;

      if (
        minValue !== null && maxValue !== null &&
        valGte === minValue && valLte === maxValue
      ) {
        // Skip both min and max values by not processing these keys
        continue;
      }
    }

    if (inputType == 'checkbox') {
      if (element.checked) {
        if (!checkboxesToKeep[key]) {
          checkboxesToKeep[key] = [value];
          continue;
        } else {
          checkboxesToKeep[key].push(value);
          continue;
        }
      }

      if (Shopify.queryParams[key] && !checkboxesToKeep[key]) {
        delete Shopify.queryParams[key];
      }
      continue;
    } else {
      Shopify.queryParams[key] = value;
    }
  }

  if (Object.entries(checkboxesToKeep).length > 0) {
    for (const [key, value] of Object.entries(checkboxesToKeep)) {
      Shopify.queryParams[key] = value.join(',');
    }
  }

  return Shopify.queryParams;
};

/**
 * This function fetches fresh collection page via AJAX request to collection's url.
 * @param {Array} selectors - Array of HTML element selectors to update.
 */
const getCollectionAJAX = async (selectors) => {
  Shopify.queryParams.page = 1;
  delete Shopify.queryParams.cpage;
  const CollectionURLSearchParams = getCollectionURLSearchParams(),
        url = location.pathname + '?' + CollectionURLSearchParams;

  if (selectors) {
    try {
      const res = await fetch(url);
      const body = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(body, 'text/html');

      for (const selector of selectors) {
        if (!document.querySelector(selector) || !doc.querySelector(selector)) continue;
        document.querySelector(selector).innerHTML = doc.querySelector(selector).innerHTML;

        // Update apply number
        if (selector === '#product-grid-ajax') {
          let filteredCount = parseInt(doc.querySelector(selector).dataset.filteredCount, 10),
              context = doc.querySelector(selector).dataset.context,
              zeroResultsMsg = document.getElementById('facets-no-results-message'),
              resultsStringOne = window.themeStrings.product_count_simple_one,
              resultsStringOther = window.themeStrings.product_count_simple_other;

          if (context === 'search') {
            resultsStringOne = window.themeStrings.results_with_count_one;
            resultsStringOther = window.themeStrings.results_with_count_other;
          }

          if (filteredCount === 0) {
            document.getElementById('apply-count').innerHTML = resultsStringOther.replace('[count]', filteredCount);
            zeroResultsMsg.classList.remove('hide');
          } else if (filteredCount === 1) {
            document.getElementById('apply-count').innerHTML = resultsStringOne.replace('[count]', filteredCount);
            zeroResultsMsg.classList.add('hide');
          } else {
            document.getElementById('apply-count').innerHTML = resultsStringOther.replace('[count]', filteredCount);
            zeroResultsMsg.classList.add('hide');
          }
        }
      }

      window.history.replaceState('', '', url);
      applyClientSortAndPaginate('#product-grid-ajax');
      Woolman.Collection.classList.remove('processing');

    } catch (err) {
      location.search = CollectionURLSearchParams;
    }
  } else {
    location.search = CollectionURLSearchParams;
  }
};

/**
 * Returns URL for current collection. Utilizes URL parameters to save user's filters, sorting and pagination.
 * @returns
 */
const getCollectionURLSearchParams = () => {
  return new URLSearchParams(Object.entries(Shopify.queryParams)).toString().replaceAll(/%2C/g, ',');
};

// On Sale collections with no explicit sort_by yet, the "Am meisten gespart" (biggest discount)
// option is the intended default. Since discount isn't a native Shopify sort key, the initial
// server render just shows the collection's normal order; here we transparently switch to the
// full, correctly-sorted set right after load so the very first view already reflects it.
const initialSaleGrid = document.querySelector('#product-grid-ajax');
if (initialSaleGrid && initialSaleGrid.dataset.c4ySaleDefaultSort === 'true' && !Shopify.queryParams.sort_by) {
  Shopify.queryParams.sort_by = 'discount-descending';
  if (Woolman.Collection) Woolman.Collection.classList.add('processing');
  getCollectionAJAX(['#product-grid-ajax', '#pagination-ajax']);
} else {
  applyClientSortAndPaginate();
}

class SortBySelect extends HTMLElement {
  constructor() {
    'use strict';
    super();

    // Find select element or radio buttons group within the custom element
    this.el = this.querySelector('select') || this.querySelectorAll('input[type="radio"]');
    this.selectors = {
      grid: '#product-grid-ajax',
      pagination: '#pagination-ajax',
    };

    // Initialize value based on current query parameters
    const sortByValue = Shopify.queryParams['sort_by'];
    if (sortByValue) {
      if (this.el instanceof HTMLSelectElement) {
        this.el.value = sortByValue;
      } else if (this.el instanceof NodeList) {
        this.el.forEach(radio => {
          if (radio.value === sortByValue) {
            radio.checked = true;
          }
        });
      }
    }

    // Add event listeners for the select or radio buttons
    if (this.el instanceof HTMLSelectElement) {
      this.el.addEventListener('change', this.onSelectChange.bind(this));
    } else if (this.el instanceof NodeList) {
      this.el.forEach(radio => {
        radio.addEventListener('change', this.onSelectChange.bind(this));
      });
    }
  }

  connectedCallback() {
    this.syncInputs();
  }

  syncInputs() {
    const allSortBySelects = document.querySelectorAll('sort-by');
    if (this.el instanceof NodeList) {
      this.el.forEach(radio => {
        radio.addEventListener('change', () => {
          allSortBySelects.forEach((sortBySelect) => {
            if (sortBySelect !== this) {
              const correspondingRadio = sortBySelect.querySelector(`input[type="radio"][value="${radio.value}"]`);
              if (correspondingRadio) {
                correspondingRadio.checked = radio.checked;
              }
            }
          });
        });
      });
    }
  }

  async onSelectChange(event) {
    Woolman.Collection.classList.add('processing');
    Woolman.Utils.prepareQueryParams();
    const value = event.target.value;
    Shopify.queryParams.sort_by = value;
    await getCollectionAJAX(Object.values(this.selectors));
  }
}

if (customElements.get('sort-by') === undefined) {
  customElements.define('sort-by', SortBySelect);
}

class FacetsForm extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this.querySelector('form');
    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
    };

    const inputs = this.el.querySelectorAll('input');
    inputs.forEach((input) => {
      input.addEventListener('change', debounce(this.onInputChange.bind(this), 150));
    });

    this.el.addEventListener('submit', this.onFormSubmit.bind(this));
  }

  connectedCallback() {
    this.syncInputs();
  }

  syncInputs() {
    const allFacetsForms = document.querySelectorAll('facets-form');
    const inputs = this.el.querySelectorAll('input');

    inputs.forEach((input) => {
      input.addEventListener('change', () => {
        allFacetsForms.forEach((form) => {
          if (form !== this) {
            const correspondingInput = form.querySelector(`input[name="${input.name}"][value="${input.value}"]`);
            if (correspondingInput) {
              correspondingInput.checked = input.checked;
            }
          }
        });
      });
    });
  }

  async onFormSubmit(event) {
    event.preventDefault();
    Woolman.Collection.classList.add('processing');
    Woolman.Utils.prepareQueryParams();
    generateSearchParamsFromFormData(this.el);

    Woolman.ModalsAndDrawers.closeModalOrDrawer(this.closest('[data-drawer]').getAttribute('id'));
    getCollectionAJAX(Object.values(this.selectors));
  }

  async onInputChange() {
    Woolman.Collection.classList.add('processing');
    generateSearchParamsFromFormData(this.el);
    getCollectionAJAX(Object.values(this.selectors));
  }
}

if (customElements.get('facets-form') === undefined) {
  customElements.define('facets-form', FacetsForm);
}

class CurrentFacetButton extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
    };

    this.btn = this.querySelector('a');
    this.btn.addEventListener('click', this.removeFacet.bind(this));
  }

  async removeFacet(event) {
    event.preventDefault();
    if (event.currentTarget.dataset.filterParamName === 'filter.v.price') {
      const price_slider = document.querySelector('price-range-slider');
      delete Shopify.queryParams['filter.v.price.lte'];
      delete Shopify.queryParams['filter.v.price.gte'];
      price_slider.reset();
      getCollectionAJAX(Object.values(this.selectors));
    } else {
      const inputToReset = document.querySelector(`.filter-form [data-key-value="${ event.currentTarget.dataset.filterParamName }:${ event.currentTarget.dataset.filterValue }"]`);
      inputToReset.checked = false;
      inputToReset.dispatchEvent(new Event('change'));
    }
  }
}
if (customElements.get('current-facet-button') === undefined) {
  customElements.define('current-facet-button', CurrentFacetButton);
}
